/**
 * Contract Hub collaborate API for the Word add-in.
 * Backend: GET /api/collaborate/contracts/{contractId}/threads and
 * POST /api/collaborate/versions/{versionId}/threads, etc.
 */
import { getAgreementIdFromDocument, getAgreementVersionIdFromDocument } from "../agreement";
import { authApi } from "./auth";
import { apiRequest } from "./client";
import type {
  CollaborateApi,
  ThreadFilterSummary,
  ThreadListPage,
  ThreadListQuery,
} from "../../collaborate/api/types";
import { COLLABORATE_THREAD_PAGE_SIZE } from "../../collaborate/api/types";
import type { CollaborateUser } from "../../collaborate/types/collaborate";
import type {
  CommentReply,
  CreateThreadInput,
  InboxSummary,
  Thread,
  ThreadStatus,
  UserId,
  AnchorDescriptor,
} from "../../collaborate/types/collaborateThread";
import { listViewToApiParams } from "../../collaborate/utils/listViewToApiParams";
import { wrapMentionsInBody } from "../../collaborate/utils/wrapMentionsInBody";

const COLLABORATE_API = "/api/collaborate";

type ProjectMemberRow = {
  id: string;
  full_name?: string;
  fullName?: string;
  email: string;
};

function mapProjectMember(row: ProjectMemberRow): CollaborateUser {
  return {
    id: row.id,
    name: (row.full_name ?? row.fullName ?? row.email).trim(),
    email: row.email,
  };
}

async function resolveVersionId(documentId?: string): Promise<string> {
  if (documentId?.trim()) {
    return documentId.trim();
  }
  return getAgreementVersionIdFromDocument();
}

async function resolveContractId(documentId?: string): Promise<string> {
  if (documentId?.trim()) {
    return documentId.trim();
  }
  return getAgreementIdFromDocument();
}

export function createContractHubCollaborateApi(): CollaborateApi {
  return {
    async fetchUsers(): Promise<CollaborateUser[]> {
      const rows = await apiRequest<ProjectMemberRow[]>("/api/project-members", { method: "GET" });
      return rows.map(mapProjectMember);
    },

    async fetchCurrentUser(): Promise<CollaborateUser> {
      const me = await authApi.me();
      return { id: me.id, name: me.name, email: me.email };
    },

    async fetchThreadSummary(
      documentId?: string,
      query: Pick<ThreadListQuery, "listView" | "versionFilter"> = {
        listView: "comments",
        versionFilter: null,
      },
    ): Promise<ThreadFilterSummary> {
      const contractId = await resolveContractId(documentId);
      const qs = new URLSearchParams({ listView: query.listView });
      if (query.versionFilter !== null && query.versionFilter !== undefined) {
        qs.set("versionNumber", String(query.versionFilter));
      }
      return apiRequest<ThreadFilterSummary>(
        `${COLLABORATE_API}/contracts/${encodeURIComponent(contractId)}/threads/summary?${qs.toString()}`,
        { method: "GET" },
      );
    },

    async fetchThreadsPage(
      query: ThreadListQuery,
      documentId?: string,
    ): Promise<ThreadListPage> {
      const contractId = await resolveContractId(documentId);
      const params = listViewToApiParams(query.listView);
      const qs = new URLSearchParams({
        tab: params.tab,
        filter: params.filter,
        scope: params.scope,
        limit: String(query.limit ?? COLLABORATE_THREAD_PAGE_SIZE),
      });
      if (query.versionFilter !== null && query.versionFilter !== undefined) {
        qs.set("versionNumber", String(query.versionFilter));
      }
      if (query.cursor) {
        qs.set("cursor", query.cursor);
      }
      return apiRequest<ThreadListPage>(
        `${COLLABORATE_API}/contracts/${encodeURIComponent(contractId)}/threads?${qs.toString()}`,
        { method: "GET" },
      );
    },

    async fetchInboxSummary(documentId?: string): Promise<InboxSummary> {
      const contractId = await resolveContractId(documentId);
      return apiRequest<InboxSummary>(
        `${COLLABORATE_API}/contracts/${encodeURIComponent(contractId)}/inbox-summary`,
        { method: "GET" },
      );
    },

    async getThread(threadId: string): Promise<Thread> {
      return apiRequest<Thread>(
        `${COLLABORATE_API}/threads/${encodeURIComponent(threadId)}`,
        { method: "GET" },
      );
    },

    async createThread(input: CreateThreadInput): Promise<Thread> {
      const versionId = input.agreementVersionId?.trim()
        ? input.agreementVersionId.trim()
        : await resolveVersionId();
      return apiRequest<Thread>(
        `${COLLABORATE_API}/versions/${encodeURIComponent(versionId)}/threads`,
        {
          method: "POST",
          jsonBody: {
            quote: input.snippet?.quote?.trim() ?? "",
            body: wrapMentionsInBody(input.body),
            mentions: input.mentions?.length ? input.mentions : undefined,
            requireApproval: input.requireApproval,
            assignee: input.requireApproval && input.assignee ? { id: input.assignee.id } : null,
            playbookAssignee:
              input.playbookAssignee && input.body.includes("<playbook>")
                ? { id: input.playbookAssignee.id }
                : null,
            versionTagged: input.versionTagged !== false,
            anchor: input.anchor
              ? {
                  type: input.anchor.type,
                  id: input.anchor.id,
                  versionId: input.anchor.versionId,
                  quoteText: input.anchor.quoteText,
                  normalizedQuote: input.anchor.normalizedQuote,
                  quoteHash: input.anchor.quoteHash,
                }
              : undefined,
          },
        },
      );
    },

    async addReply(
      threadId: string,
      body: string,
      mentions?: UserId[],
      agreementVersionId?: string,
      versionTagged?: boolean,
    ): Promise<CommentReply> {
      return apiRequest<CommentReply>(
        `${COLLABORATE_API}/threads/${encodeURIComponent(threadId)}/replies`,
        {
          method: "POST",
          jsonBody: {
            body,
            mentions: mentions?.length ? mentions : undefined,
            agreementVersionId: agreementVersionId?.trim() || undefined,
            versionTagged: versionTagged !== false,
          },
        },
      );
    },

    async patchThreadStatus(threadId: string, status: ThreadStatus): Promise<Thread> {
      if (status !== "approved" && status !== "rejected") {
        throw new Error("Only approved or rejected status can be patched.");
      }
      return apiRequest<Thread>(
        `${COLLABORATE_API}/threads/${encodeURIComponent(threadId)}/status`,
        {
          method: "PATCH",
          jsonBody: { status },
        },
      );
    },

    async markThreadRead(threadId: string): Promise<void> {
      await apiRequest<void>(
        `${COLLABORATE_API}/threads/${encodeURIComponent(threadId)}/read`,
        { method: "POST" },
      );
    },

    async fetchPendingFocus(versionId: string): Promise<{ rootCommentId: string | null }> {
      return apiRequest<{ rootCommentId: string | null }>(
        `${COLLABORATE_API}/versions/${encodeURIComponent(versionId)}/focus`,
        { method: "GET" },
      );
    },

    async patchThreadAnchor(threadId: string, anchor: AnchorDescriptor): Promise<Thread> {
      return apiRequest<Thread>(
        `${COLLABORATE_API}/threads/${encodeURIComponent(threadId)}/anchor`,
        {
          method: "PATCH",
          jsonBody: {
            type: anchor.type,
            id: anchor.id,
            versionId: anchor.versionId,
            quoteText: anchor.quoteText,
            normalizedQuote: anchor.normalizedQuote,
            quoteHash: anchor.quoteHash,
          },
        },
      );
    },
  };
}
